import { BackButton } from "@/components/BackButton";
import React from "react";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

// Full-screen branded intro used for the website welcome and each course intro.
export const IntroSplash = ({ eyebrow, title, subtitle, image, video, ctaLabel = "Enter", onEnter, testId = "intro-splash", back }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={testId}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-y-auto bg-secondary px-6 py-10 text-center"
    >
      {back && <div className="absolute left-5 top-5 z-10"><BackButton testId={`${testId}-back-button`} fallback={back} /></div>}
      {/* soft orange glow */}
      <div className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(circle at 50% 40%, rgba(224,145,47,0.25), transparent 60%)" }} />

      <motion.div
        initial={{ scale: 0.85, y: 12, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10"
      >
        <Logo height={130} rounded="rounded-2xl" className="dark:shadow-2xl dark:shadow-black/40" />
      </motion.div>

      {eyebrow && (
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
          className="relative z-10 mt-10 font-mono text-xs uppercase tracking-[0.35em] text-primary"
        >
          {eyebrow}
        </motion.p>
      )}
      {title && (
        <motion.h1
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="relative z-10 mt-3 max-w-2xl font-serif text-3xl leading-tight text-foreground sm:text-4xl lg:text-5xl"
        >
          {title}
        </motion.h1>
      )}
      {subtitle && (
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
          className="relative z-10 mt-4 max-w-md text-muted-foreground"
        >
          {subtitle}
        </motion.p>
      )}

      {video ? (
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="relative z-10 mt-7 w-full max-w-[440px] overflow-hidden rounded-2xl border border-white/15 shadow-2xl shadow-black/50"
        >
          <video
            src={video}
            poster={image}
            data-testid="intro-video"
            autoPlay
            muted
            loop
            playsInline
            controls
            className="h-auto w-full bg-black"
          />
        </motion.div>
      ) : image ? (
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="relative z-10 mt-7 w-full max-w-[320px] overflow-hidden rounded-2xl border border-white/15 shadow-2xl shadow-black/50"
        >
          <img src={image} alt="The JT Band" className="w-full object-cover" draggable={false} />
        </motion.div>
      ) : null}

      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
        className="relative z-10 mt-9"
      >
        <Button
          onClick={onEnter}
          data-testid="intro-enter-button"
          className="rounded-full bg-primary px-10 py-6 text-base font-medium text-foreground hover:bg-primary/90"
        >
          {ctaLabel}
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default IntroSplash;
